import { createSelector } from 'reselect';
import { fromJS } from 'immutable';
import { getClientById } from 'selectors/entities/clientsSelectors';

import {
  getAllUsers,
  getClientProfiles,
  getUsersWithDepartment,
  getUsersWithClients,
} from 'selectors/entities/userSelectors';
import {
  getChecklistItems,
  getChecklists,
  getTaskById,
  getTaskByIdWithParent,
  getTaskLabels,
  getTaskStatuses,
  getTasksWithSummary,
  getTaskSchedules,
} from '../entities/taskSelectors';
import { getProjects, getTeamMaps } from '../entities/projectsSelectors';
import { getLegalEntities } from 'selectors/entities/legalEntitiesSelectors';
import { getFiles } from 'selectors/entities/fileSelector';

export const getChecklistByTaskId = createSelector(
  [getTaskById, getChecklists, getChecklistItems],
  (task, checklists, checklistItems) => {
    if (!task) {
      return null;
    }

    if (!checklists || !task.get('checklistId')) {
      return null;
    }

    const checklist = checklists.get(task.get('checklistId'));
    if (!checklist || !checklist.get('listItems')) {
      return null;
    }

    return checklist.set(
      'listItems',
      checklist
        .get('listItems')
        .map((id) => checklistItems.get(id))
        .sort((a, b) => (a.get('position') < b.get('position') ? -1 : 1)),
    );
  },
);

// Returns task by id with summary
export const getTaskByIdWithSummary = createSelector(
  [
    getTaskById,
    getAllUsers,
    getUsersWithDepartment,
    getProjects,
    getTaskStatuses,
    getTaskLabels,
    getClientProfiles,
    getTeamMaps,
    getTasksWithSummary,
    getChecklists,
    getChecklistItems,
    getTaskSchedules,
    getFiles,
  ],
  (
    task,
    usersWithDeleted,
    users,
    projects,
    statuses,
    labels,
    clientProfiles,
    teamMap,
    tasks,
    checklists,
    checklistItems,
    schedules,
    allFiles,
  ) => {
    if (!task) {
      return null;
    }

    let taskStatus = null,
      taskLabels = [],
      spectators = [],
      privacyUsers = [],
      project = null,
      user = null,
      creator = null,
      coPerformer = null,
      checklist = null,
      schedule = null,
      files = [];

    const allUsers = users.merge(clientProfiles);

    if (allUsers && task.get('userId')) {
      user = allUsers.get(task.get('userId'));
      user = !user ? usersWithDeleted.get(task.get('userId')) : user;
    }

    if (allUsers && task.get('creatorId')) {
      creator = allUsers.get(task.get('creatorId'));
      creator = !creator ? usersWithDeleted.get(task.get('creatorId')) : creator;
    }

    if (allUsers && task.get('coPerformerId')) {
      coPerformer = allUsers.get(task.get('coPerformerId'));
    }

    if (statuses && task.get('statusId')) {
      taskStatus = statuses.get(task.get('statusId'));
    }

    if (labels && task.get('labels')) {
      taskLabels = task.get('labels').map((id) => labels.get(id));
    }

    if (users && task.get('privacyUsers')) {
      privacyUsers = task
        .get('privacyUsers')
        .map((id) => fromJS({ user: allUsers.get(id) }))
        .filter((user) => user.get('user'));
    }

    if (users && task.get('spectators')) {
      spectators = task
        .get('spectators')
        .map((id) => fromJS({ user: allUsers.get(id) }))
        .filter((user) => user.get('user'));
    }

    if (checklists && task.get('checklistId')) {
      checklist = checklists.get(task.get('checklistId'));

      if (checklist && checklist.get('listItems') && checklistItems) {
        checklist = checklist.set(
          'listItems',
          checklist
            .get('listItems')
            .map((id) => checklistItems.get(id))
            .sort((a, b) => (a.get('position') < b.get('position') ? -1 : 1)),
        );
      }
    }

    if (projects && task.get('projectId')) {
      project = projects.get(task.get('projectId'));
    }

    if (project?.get('team') && teamMap) {
      let team = project
        ?.get('team')
        .map((id) => teamMap.get(id))
        .map((map) => {
          if (map) {
            if (map.get('type') !== 'client') {
              return map.set('user', users.get(map.get('userId')));
            } else {
              return map.set('user', clientProfiles.get(map.get('userId')));
            }
          }

          return null;
        })
        .filter((map) => map && map.get('user'));

      let companyUser = null;

      if (users) {
        companyUser = users.find((user) => user.get('userType') === 'company');
      }

      project = project?.merge(
        fromJS({
          companyUser,
          team,
        }),
      );
    }

    if (schedules && task.get('scheduleId')) {
      schedule = schedules.get(task.get('scheduleId'));
    }

    if (allFiles && task.get('files')?.size) {
      files = task
        .get('files')
        .map((fileId) => allFiles.get(fileId))
        .filter((file) => file);
    }

    const mergeSubTasks = (task, tasks) => {
      if (task) {
        task = task.set('project', project);
        if (task.get('subTasks')?.size) {
          const subTasks = task.get('subTasks').map((id) => mergeSubTasks(tasks.get(id), tasks));
          return task.set('subTasks', subTasks);
        }
      }
      return task;
    };

    return mergeSubTasks(task, tasks).merge(
      fromJS({
        labels: taskLabels,
        user,
        creator,
        coPerformer,
        spectators,
        privacyUsers,
        project,
        taskStatus,
        checklist,
        schedule,
        files,
      }),
    );
  },
);

// Returns task by id with summary without 'checklist'
export const getTaskByIdWithSummaryWithoutChecklist = createSelector(
  [
    getTaskByIdWithParent,
    getAllUsers,
    getUsersWithDepartment,
    getProjects,
    getTaskStatuses,
    getTaskLabels,
    getClientProfiles,
    getTeamMaps,
    getTasksWithSummary,
    getTaskSchedules,
    getFiles,
  ],
  (task, usersWithDeleted, users, projects, statuses, labels, clientProfiles, teamMap, tasks, schedules, allFiles) => {
    if (!task) {
      return null;
    }

    let taskStatus = null,
      taskLabels = [],
      spectators = [],
      privacyUsers = [],
      project = null,
      user = null,
      creator = null,
      coPerformer = null,
      schedule = null,
      files = [];

    const projectId = task.get('projectId');
    if (projects && projectId) {
      project = projects.get(projectId);
    }

    const allUsers = users.merge(
      clientProfiles.filter((client) => client.get('clientId') === project?.get('clientId')),
    );

    if (allUsers && task.get('userId')) {
      user = allUsers.get(task.get('userId'));
      user = !user ? usersWithDeleted.get(task.get('userId')) : user;
    }

    if (allUsers && task.get('creatorId')) {
      creator = allUsers.get(task.get('creatorId'));
      creator = !creator ? usersWithDeleted.get(task.get('creatorId')) : creator;
    }

    if (allUsers && task.get('coPerformerId')) {
      coPerformer = allUsers.get(task.get('coPerformerId'));
    }

    if (statuses && task.get('statusId')) {
      taskStatus = statuses.get(task.get('statusId'));
    }

    if (labels && task.get('labels')) {
      taskLabels = task.get('labels').map((id) => labels.get(id));
    }

    if (users && task.get('privacyUsers')) {
      privacyUsers = task
        .get('privacyUsers')
        .map((id) => fromJS({ user: allUsers.get(id) }))
        .filter((user) => user.get('user'));
    }

    if (users && task.get('spectators')) {
      spectators = task
        .get('spectators')
        .map((id) => fromJS({ user: allUsers.get(id) }))
        .filter((user) => user.get('user'));
    }

    if (project?.get('team') && teamMap) {
      let team = project
        ?.get('team')
        .map((id) => teamMap.get(id))
        .map((map) => {
          if (map) {
            if (map.get('type') !== 'client') {
              return map.set('user', users.get(map.get('userId')));
            } else {
              return map.set('user', clientProfiles.get(map.get('userId')));
            }
          }

          return null;
        })
        .filter((map) => map && map.get('user'));

      let companyUser = null;

      if (users) {
        companyUser = users.find((user) => user.get('userType') === 'company');
      }

      project = project?.merge(
        fromJS({
          companyUser,
          team,
        }),
      );
    }

    if (schedules && task.get('scheduleId')) {
      schedule = schedules.get(task.get('scheduleId'));
    }

    if (allFiles && task.get('files')?.size) {
      files = task
        .get('files')
        .map((fileId) => allFiles.get(fileId))
        .filter((file) => file);
    }

    const mergeSubTasks = (task, tasks) => {
      if (task) {
        task = task.set('project', project);
        if (task.get('subTasks')?.size) {
          const subTasks = task.get('subTasks').map((id) => mergeSubTasks(tasks.get(id), tasks));
          return task.set('subTasks', subTasks);
        }
      }
      return task;
    };

    return mergeSubTasks(task, tasks).merge(
      fromJS({
        labels: taskLabels,
        user,
        creator,
        coPerformer,
        spectators,
        privacyUsers,
        project,
        taskStatus,
        schedule,
        files,
      }),
    );
  },
);

// Return client by id with projects
export const getClientByIdWithSummary = createSelector(
  [getProjects, getClientById, getLegalEntities, getClientProfiles],
  (projects, client, legalEntities, clientProfiles) => {
    if (client) {
      let entities = [];
      let clientProjects = [];
      let profiles = [];

      if (projects && client.get('projects')) {
        clientProjects = projects.filter((project) => project.get('clientId') === client.get('companyId'));
      }

      if (legalEntities && client.get('entities')) {
        entities = client.get('entities').map((id) => legalEntities.get(id));
      }

      if (clientProfiles && client.get('clientProfiles')) {
        profiles = client.get('clientProfiles').map((id) => clientProfiles.get(id));
      }

      return client
        .set('projects', clientProjects)
        .set('clientProfiles', profiles)
        .set('entities', entities);
    }
    return client;
  },
);

export const getTaskByIdWithTeam = createSelector(
  [getTaskById, getTeamMaps, getUsersWithClients],
  (task, teamMaps, users) => {
    let preparedTask = task;
    if (preparedTask?.get('projectId') && teamMaps) {
      preparedTask = preparedTask.set(
        'team',
        teamMaps
          .filter((map) => map.get('projectId') === preparedTask?.get('projectId'))
          ?.map((map) => users.get(map.get('userId')))
          ?.filter((map) => map)
          ?.toList(),
      );
    }
    return preparedTask;
  },
);
